import { useEffect, useLayoutEffect } from "react";
import { Cpu, ShieldCheck, Users } from "lucide-react";
import SlideGuide, { type GuideSlide, type GuideStep } from "@/components/training/SlideGuide";
import {
  TREND_CATEGORIES,
  WEEKLY_TRENDS as edition,
  type TrendCapture,
  type TrendCoverage,
  type TrendSource,
  type WeeklyTrend,
} from "@/data/weeklyTrends";
import "./Trends.css";

const icons = { tools: Cpu, governance: ShieldCheck, adoption: Users };
const activeCategories = TREND_CATEGORIES.filter((category) => edition.trends.some((trend) => trend.category === category.id));
const steps: GuideStep[] = activeCategories.map((category) => ({
  id: category.id, title: category.title, description: category.scope, duration: "10 min",
  whyItMatters: category.lens, actions: [category.lens], checkpoint: "Choose the next move.", icon: icons[category.id],
}));
const mainTrend = edition.trends.find((trend) => trend.id === "independent-ai-evaluators")!;
const zuckerbergCoverage = edition.coverage.find((item) => item.id === "zuckerberg-response")!;
const slides: GuideSlide[] = [
  { id: "cover", title: edition.headline, kind: "custom", tone: "blue" },
  { id: "weekly-brief", title: edition.brief.title, kind: "custom", tone: "paper" },
  { id: "why-now", title: edition.whyNow.title, kind: "custom", tone: "dark" },
  { id: mainTrend.id, title: mainTrend.headline, stepId: mainTrend.category, kind: "custom", tone: "dark" },
  { id: "this-weeks-move", title: edition.investigation.title, kind: "custom", tone: "dark" },
  { id: "leader-responses", title: edition.leaders.title, kind: "custom", tone: "paper" },
  { id: zuckerbergCoverage.id, title: zuckerbergCoverage.title, kind: "custom", tone: "paper" },
  { id: "open-weight-adoption", title: edition.competition.title, kind: "custom", tone: "paper" },
  { id: "who-benefits", title: edition.incentives.title, kind: "custom", tone: "dark" },
  { id: "judge-the-rules", title: edition.judgment.title, kind: "custom", tone: "dark" },
  { id: "sources", title: "Evidence & reading", kind: "custom", tone: "paper" },
];

const seenSources = new Set<string>();
const readingSources = [
  ...edition.trends.flatMap((trend) => [...trend.sources, ...(trend.captures ?? []).map((capture) => capture.source)]),
  ...edition.coverage.flatMap((item) => item.captures.map((capture) => capture.source)),
  ...edition.investigation.sources,
  ...edition.investigation.cases.map((item) => item.source),
  ...edition.leaders.items.map((leader) => leader.capture.source),
  ...edition.competition.sources,
  edition.competition.capture.source,
  ...edition.whyNow.sources,
  ...edition.whyNow.timeline.map((event) => event.source),
  ...edition.incentives.sources,
].filter((source) => {
  if (seenSources.has(source.url)) return false;
  seenSources.add(source.url);
  return true;
});

const LEGACY_SLIDE_HASHES: Record<string, string> = {
  "#in-the-news": "#zuckerberg-response",
  "#altman-response": "#leader-responses",
  "#other-leaders": "#leader-responses",
};
const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

function SourceDates({ source }: { source: TrendSource }) {
  return <span>{source.published ? `Published ${source.published}` : "Exact publication date not shown"} · Checked {source.checked}</span>;
}

function EvidenceLinks({ sources, label = "Sources" }: { sources: TrendSource[]; label?: string }) {
  return <div className="trends-evidence"><strong>{label}</strong>{sources.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noopener noreferrer">{source.title} ↗</a>)}</div>;
}

function ScreenshotCapture({ capture, printMode }: { capture: TrendCapture; printMode: boolean }) {
  return <figure className="trends-capture">
    <a className="trends-capture-image-link" href={capture.src} target="_blank" rel="noopener noreferrer" aria-label={`Open full-size screenshot excerpt: ${capture.caption}`}>
      <img src={capture.src} width={capture.width} height={capture.height} alt={capture.alt} loading={printMode ? "eager" : "lazy"} decoding="async" />
    </a>
    <figcaption>
      <strong>Screenshot excerpt</strong>
      <p>{capture.caption}</p>
      <a href={capture.source.url} target="_blank" rel="noopener noreferrer">{capture.source.title} ↗</a>
      <SourceDates source={capture.source} />
    </figcaption>
  </figure>;
}

function TrendDetail({ trend, printMode }: { trend: WeeklyTrend; printMode: boolean }) {
  const hasCaptures = Boolean(trend.captures?.length);
  return <>
    <div className="trends-meta"><span>{activeCategories.find((category) => category.id === trend.category)?.title}</span><span className="trends-tag">{edition.status === "template" ? "Example disposition" : "Next move"}: {trend.signal}</span></div>
    <h1>{trend.headline}</h1>
    <div className={`trends-detail ${hasCaptures ? "trends-detail-with-capture" : ""}`}>
      {hasCaptures && <div className="trends-captures">{trend.captures?.map((capture) => <ScreenshotCapture key={capture.src} capture={capture} printMode={printMode} />)}</div>}
      <dl className="trends-analysis">
        {[["What changed", trend.changed], ["Why it matters", trend.implication], ["Your next move", trend.nextMove], ["Keep in view", trend.caveat]].map(([label, value]) => <div key={label}><dt className="trends-label">{label}</dt><dd>{value}</dd></div>)}
      </dl>
    </div>
    <EvidenceLinks sources={trend.sources} label={`Evidence: ${trend.evidence}`} />
  </>;
}

function CoverageDetail({ coverage, printMode }: { coverage: TrendCoverage; printMode: boolean }) {
  return <>
    <p className="trends-label">Governance & safety / {coverage.eyebrow}</p>
    <h1>{coverage.title}</h1>
    <div className="trends-coverage">
      <div className="trends-captures">{coverage.captures.map((capture) => <ScreenshotCapture key={capture.src} capture={capture} printMode={printMode} />)}</div>
      <div className="trends-coverage-analysis">
        <p className="trends-coverage-summary">{coverage.summary}</p>
        <ul>{coverage.points.map((point) => <li key={point}>{point}</li>)}</ul>
        {coverage.caveat && <div className="trends-caveat"><p className="trends-label">Keep in view</p><p>{coverage.caveat}</p></div>}
      </div>
    </div>
  </>;
}

function WhyNowDetail() {
  const whyNow = edition.whyNow;
  return <>
    <p className="trends-label">{whyNow.eyebrow}</p>
    <h1>{whyNow.title}</h1>
    <ol className="trends-timeline" aria-label="Event chronology">
      {whyNow.timeline.map((event) => <li key={`${event.date}-${event.title}`}>
        <time className="trends-timeline-date" dateTime={event.source.published ?? undefined}>{event.date}</time>
        <span className="trends-timeline-track" aria-hidden="true"><span className="trends-timeline-marker" /></span>
        <h2><a href={event.source.url} target="_blank" rel="noopener noreferrer" title={event.source.title}>{event.title} ↗</a></h2>
        <p>{event.body}</p>
      </li>)}
    </ol>
    <p className="trends-rationale">{whyNow.rationale}</p>
    <p className="trends-why-now-boundary">{whyNow.boundary}</p>
    <EvidenceLinks sources={whyNow.sources} />
  </>;
}

function InvestigationDetail() {
  const investigation = edition.investigation;
  return <>
    <p className="trends-label">{investigation.eyebrow}</p><h1>{investigation.title}</h1>
    <div className="trends-cases">
      {investigation.cases.map((item) => <article key={item.title}>
        <h2><a href={item.source.url} target="_blank" rel="noopener noreferrer" title={item.source.title}>{item.title} ↗</a></h2>
        <dl>
          {[["Setup", item.setup], ["Finding", item.finding]].map(([label, value]) => <div key={label}><dt className="trends-label">{label}</dt><dd>{value}</dd></div>)}
        </dl>
      </article>)}
    </div>
    <p className="trends-investigation-limit">{investigation.takeaway}</p>
    <EvidenceLinks sources={investigation.sources} />
  </>;
}

function LeadersDetail({ printMode }: { printMode: boolean }) {
  const leaders = edition.leaders;
  return <>
    <p className="trends-label">{leaders.eyebrow}</p><h1>{leaders.title}</h1>
    <div className="trends-leaders">
      {leaders.items.map((leader) => <article key={leader.name}>
        <h2>{leader.name}</h2>
        <p className="trends-leader-stance">{leader.stance}</p>
        <ScreenshotCapture capture={leader.capture} printMode={printMode} />
        <p className="trends-leader-detail">{leader.detail}</p>
        <p className="trends-leader-caveat">{leader.caveat}</p>
      </article>)}
    </div>
    <p className="trends-takeaway">{leaders.takeaway}</p>
  </>;
}

function CompetitionDetail({ printMode }: { printMode: boolean }) {
  const competition = edition.competition;
  return <>
    <p className="trends-label">{competition.eyebrow}</p>
    <h1>{competition.title}</h1>
    <div className="trends-competition">
      <ScreenshotCapture capture={competition.capture} printMode={printMode} />
      <dl className="trends-competition-analysis">
        <div><dt className="trends-label">Open weights, explained</dt><dd>{competition.definition}</dd></div>
        <div><dt className="trends-label">Reported adoption <span>Published evidence</span></dt><dd>{competition.adoption}</dd></div>
        <div><dt className="trends-label">Business-model pressure <span>Analysis</span></dt><dd>{competition.pressure}</dd></div>
      </dl>
    </div>
    <p className="trends-competition-caveat"><strong>Keep in view.</strong> {competition.caveat}</p>
    <EvidenceLinks sources={competition.sources} />
  </>;
}

function IncentivesDetail() {
  const incentives = edition.incentives;
  return <>
    <p className="trends-label">{incentives.eyebrow}</p><h1>{incentives.title}</h1>
    <ol className="trends-incentive-chain">
      {incentives.chain.map((item, index) => <li key={item.label}><span className="trends-number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span><h2>{item.label}</h2><p>{item.body}</p></li>)}
    </ol>
    <p className="trends-incentive-hypothesis">{incentives.hypothesis}</p>
    <p className="trends-why-now-boundary">{incentives.boundary}</p>
    <p className="trends-closing-question">{incentives.question}</p>
    <EvidenceLinks sources={incentives.sources} />
  </>;
}

function JudgmentDetail() {
  const judgment = edition.judgment;
  return <>
    <p className="trends-label">{judgment.eyebrow}</p><h1>{judgment.title}</h1>
    <div className="trends-judgment">
      {judgment.criteria.map((criterion, index) => <section key={criterion.title}><span className="trends-number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span><h2>{criterion.title}</h2><p>{criterion.question}</p></section>)}
    </div>
    <p className="trends-judgment-takeaway">{judgment.takeaway}</p>
  </>;
}

function renderSlide(slide: GuideSlide, { light, printMode }: { light: boolean; printMode: boolean }) {
  return <div data-trends-slide={slide.id} className={`trends-slide ${light ? "trends-light" : "trends-dark"} ${[mainTrend.id, zuckerbergCoverage.id, "leader-responses", "open-weight-adoption"].includes(slide.id) ? "trends-evidence-slide" : ""}`}>
    {printMode && <div className="trends-print-brand"><img src="/serve2perform-logo.png" alt="Serve2Perform" width={506} height={80} /></div>}
    {slide.id === "cover" ? <>
      <div className="trends-cover">
        <p className="trends-label">Governance & safety</p>
        <h1>{edition.headline}</h1>
        <p className="trends-intro">{edition.introduction}</p>
      </div>
      <div className="trends-bottom"><span>Verification → The evidence → Judging the rules</span><a href="#weekly-brief">Read the brief →</a></div>
    </> : slide.id === "weekly-brief" ? <>
      <p className="trends-label">Governance & safety / {edition.weekLabel}</p><h1>{edition.brief.title}</h1>
      <div className="trends-brief">{edition.brief.items.map((item, index) => <div className="trends-brief-row" key={item.href}><span className="trends-number">{String(index + 1).padStart(2, "0")}</span><h2><a href={item.href}>{item.label} ↗</a></h2><p>{item.body}</p></div>)}</div>
      <p className="trends-brief-distinction">{edition.brief.distinction}</p>
      <p className="trends-takeaway"><strong>The takeaway</strong>{edition.takeaway}</p>
    </> : slide.id === "why-now" ? <WhyNowDetail />
      : slide.id === mainTrend.id ? <TrendDetail trend={mainTrend} printMode={printMode} />
        : slide.id === "this-weeks-move" ? <InvestigationDetail />
          : slide.id === "leader-responses" ? <LeadersDetail printMode={printMode} />
            : slide.id === zuckerbergCoverage.id ? <CoverageDetail coverage={zuckerbergCoverage} printMode={printMode} />
              : slide.id === "open-weight-adoption" ? <CompetitionDetail printMode={printMode} />
                : slide.id === "who-benefits" ? <IncentivesDetail />
                  : slide.id === "judge-the-rules" ? <JudgmentDetail /> : <>
                    <p className="trends-label">Announcement, responses, investigation & adoption</p><h1>Evidence & reading.</h1>
                    <ul className="trends-source-list">{readingSources.map((source) => <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer">{source.title} ↗</a><SourceDates source={source} /></li>)}</ul>
                    <div className="trends-bottom"><p>{edition.status === "template" ? "Template only. Current claims require verified sources." : `Prepared by ${edition.editor} · ${edition.weekLabel}`}</p><a href="/s2p-elevate-ai">Explore ELEVATE ↗</a></div>
                  </>}
  </div>;
}

export default function Trends() {
  useBrowserLayoutEffect(() => {
    // Normalize retired links before SlideGuide reads the hash, including later
    // hash changes, without adding history entries or changing shared navigation.
    const normalizeHash = () => {
      const replacement = LEGACY_SLIDE_HASHES[window.location.hash];
      if (replacement) window.history.replaceState(window.history.state, "", replacement);
    };
    normalizeHash();
    window.addEventListener("hashchange", normalizeHash, true);
    return () => window.removeEventListener("hashchange", normalizeHash, true);
  }, []);

  return <SlideGuide
    name="Serve2Perform AI Governance" trackingName="Weekly AI Trends" canonicalPath="/trends"
    storageKey={`trends-${edition.id}-progress`} slideStorageKey={`trends-${edition.id}-slide`}
    seo={{ title: "Serve2Perform AI Governance: Safety Evidence, Competition & the Rules", description: "A Serve2Perform briefing on independent verification, the evidence from AI hacking incidents, and competition from open-weight models. Judge proposed rules by their effects on safety and choice.", author: "Serve2Perform", image: "https://www.technical-leaders.com/serve2perform-logo.png", keywords: ["Serve2Perform", "ELEVATE", "AI governance", "AI safety", "independent AI verification", "open-weight models", "AI competition", "Dario Amodei"] }}
    schema={{ description: "Evaluate independent verification, compare the evidence from AI safety incidents, examine competitive incentives, and judge proposed rules by their effects on risk and model choice.", totalTime: "PT10M" }}
    cover={{ title: edition.headline, accent: "", description: edition.introduction, badges: [], finishLineTitle: "One governance review", finishLineBody: edition.takeaway }}
    outcomes={{ eyebrow: "Governance briefing", heading: edition.brief.title, items: edition.brief.items.map((item) => item.body), summary: edition.takeaway }}
    roadmapHeading="Governance & safety" steps={steps} slides={slides}
    branding={{
      rootClassName: "trends-deck",
      logo: { src: "/serve2perform-logo.png", alt: "Serve2Perform", href: "https://serve2perform.com", ariaLabel: "Visit Serve2Perform", imageClassName: "h-auto w-32 sm:w-40", wrapperClassName: "bg-white px-3 py-2" },
      exit: { href: "https://serve2perform.com", label: "Serve2Perform" },
      contentsTitle: "The governance briefing",
      paperBackdrop: "radial-gradient(circle at 88% 16%, rgba(0,71,255,.08), transparent 25%), radial-gradient(circle at 8% 88%, rgba(95,198,216,.08), transparent 24%)",
      darkBackdrop: "radial-gradient(circle at 82% 18%, rgba(157,182,255,.18), transparent 28%), linear-gradient(115deg, transparent 60%, rgba(255,255,255,.04) 60% 62%, transparent 62% 68%, rgba(255,255,255,.04) 68% 70%, transparent 70%)",
    }}
    contentsExtras={[
      { slideId: "cover", label: "Cover" },
      { slideId: "weekly-brief", label: "Two decisions" },
      { slideId: "why-now", label: "Why now" },
      { slideId: mainTrend.id, label: "The commitment" },
      { slideId: "this-weeks-move", label: "Two incident cases" },
      { slideId: "leader-responses", label: "Three responses" },
      { slideId: "zuckerberg-response", label: "The disagreement" },
      { slideId: "open-weight-adoption", label: "The enterprise alternative" },
      { slideId: "who-benefits", label: "Who benefits?" },
      { slideId: "judge-the-rules", label: "Judge the rules" },
      { slideId: "sources", label: "Evidence & reading" },
    ]}
    renderCustomSlide={renderSlide}
    finish={{ eyebrow: "Next review", heading: "Bring the evidence back.", body: edition.takeaway, cards: [] }}
  />;
}
